import React, { useState } from 'react';
import './PrizeV2Modal.css';

const PrizeV2Modal = ({ showModal, setShowModal, prizeData }) => {
  const [expandedRounds, setExpandedRounds] = useState(new Set());

  if (!showModal || !prizeData) return null;

  const { tournament, summary, roundResults, potDistribution } = prizeData;

  const handleClose = () => {
    setShowModal(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target.className === 'prize-v2-modal-backdrop') {
      handleClose();
    }
  };

  const toggleRoundExpansion = (roundNumber) => {
    const newExpanded = new Set(expandedRounds);
    if (newExpanded.has(roundNumber)) {
      newExpanded.delete(roundNumber);
    } else {
      newExpanded.add(roundNumber);
    }
    setExpandedRounds(newExpanded);
  };

  const getPlayerBreakdown = (round) => {
    const players = new Map();

    // Process saw-off results
    if (round.sawOffResult && round.sawOffResult.paymentResults) {
      round.sawOffResult.paymentResults.forEach(payment => {
        const key = payment.golferId;
        if (!players.has(key)) {
          players.set(key, {
            golferId: payment.golferId,
            golferName: payment.golferName,
            userName: payment.userName,
            totalPrize: 0,
            skinAmount: 0,
            sawOffAmount: 0,
            skinPercentage: 0,
            skinCount: 0
          });
        }
        const player = players.get(key);
        player.sawOffAmount += payment.amount;
        player.totalPrize += payment.amount;
      });
    }

    // Process skin results
    if (round.skinResult && round.skinResult.distributionResults) {
      const totalSkins = round.skinResult.distributionResults.reduce((sum, dist) => sum + dist.skinsWon, 0);
      
      round.skinResult.distributionResults.forEach(dist => {
        const key = dist.golferId;
        if (!players.has(key)) {
          players.set(key, {
            golferId: dist.golferId,
            golferName: dist.golferName,
            userName: dist.userName,
            totalPrize: 0,
            skinAmount: 0,
            sawOffAmount: 0,
            skinPercentage: 0,
            skinCount: 0
          });
        }
        const player = players.get(key);
        player.skinAmount += dist.prizeAmount;
        player.skinCount += dist.skinsWon;
        player.skinPercentage = totalSkins > 0 ? (dist.skinsWon / totalSkins * 100) : 0;
        player.totalPrize += dist.prizeAmount;
      });
    }

    return Array.from(players.values()).sort((a, b) => b.totalPrize - a.totalPrize);
  };

  return (
    <div className="prize-v2-modal-backdrop" onClick={handleBackdropClick}>
      <div className="prize-v2-modal">
        <div className="prize-v2-modal-header">
          <h2>💰 Prize Distribution V2 - Per Round</h2>
          <button className="prize-v2-modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="prize-v2-modal-content">
          {/* Tournament Info */}
          <div className="prize-section">
            <h3>🏆 Tournament: {tournament.name}</h3>
            <div className="prize-info-grid">
              <div className="prize-info-item">
                <span className="label">Total Pot:</span>
                <span className="value total-pot">${tournament.totalPot.toFixed(2)}</span>
              </div>
            </div>
          </div>



          {/* Round Results */}
          {roundResults && roundResults.length > 0 && (
            <div className="prize-section">
              <h3>🎯 Round-by-Round Results</h3>
              <div className="rounds-results">
                {roundResults.map((round) => {
                  const isExpanded = expandedRounds.has(round.roundNumber);
                  const playerBreakdown = getPlayerBreakdown(round);
                  
                  return (
                    <div key={round.roundNumber} className="round-result-card">
                      <div 
                        className="round-header" 
                        onClick={() => toggleRoundExpansion(round.roundNumber)}
                      >
                        <div className="round-content">
                          <h4>Round {round.roundNumber}</h4>
                          <div className="round-summary">
                            <div className="detail-row">
                              <span>Starting Pot:</span>
                              <span className="amount">${round.roundPot.toFixed(2)}</span>
                            </div>
                            <div className="detail-row">
                              <span>Saw-Offs Paid:</span>
                              <span className="amount">${round.sawOffResult.amountPaid.toFixed(2)}</span>
                            </div>
                            <div className="detail-row">
                              <span>Skins Paid:</span>
                              <span className="amount">${round.skinResult.totalDistributed.toFixed(2)}</span>
                            </div>
                                   <div className="detail-row">
                                     <span>Total Skins:</span>
                                     <span className="amount">{round.skinResult.distributionResults?.reduce((sum, dist) => sum + dist.skinsWon, 0) || 0}</span>
                                   </div>
                                   <div className="detail-row">
                                     <span>Total Saw-Offs:</span>
                                     <span className="amount">{round.sawOffResult.paymentResults?.length || 0}</span>
                                   </div>
                            <div className="detail-row">
                              <span>House Keeps:</span>
                              <span className="amount">${round.houseKeeps.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="expand-icon">
                          {isExpanded ? '▼' : '▶'}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="player-breakdown">
                          <h5>Player Breakdown</h5>
                          {playerBreakdown.length > 0 ? (
                            <div className="breakdown-table">
                              <div className="breakdown-header">
                                <div className="col-player">Player</div>
                                <div className="col-user">User</div>
                                <div className="col-skin">Skin</div>
                                <div className="col-sawoff">Saw-Off</div>
                                <div className="col-total">Total</div>
                              </div>
                              {playerBreakdown.map((player, index) => (
                                <div key={`${round.roundNumber}-${player.golferId}`} className="breakdown-row">
                                  <div className="col-player">
                                    <span className="player-name">{player.golferName}</span>
                                    {player.skinCount > 0 && (
                                      <span className="skin-count">({player.skinCount} skin{player.skinCount > 1 ? 's' : ''})</span>
                                    )}
                                  </div>
                                  <div className="col-user">
                                    <span className="user-name">{player.userName}</span>
                                  </div>
                                  <div className="col-skin">
                                    {player.skinAmount > 0 ? (
                                      <>
                                        <span className="skin-amount">${player.skinAmount.toFixed(2)}</span>
                                        <span className="skin-percentage">({player.skinPercentage.toFixed(1)}%)</span>
                                      </>
                                    ) : (
                                      <span className="no-skin">—</span>
                                    )}
                                  </div>
                                  <div className="col-sawoff">
                                    {player.sawOffAmount > 0 ? (
                                      <span className="sawoff-amount">${player.sawOffAmount.toFixed(2)}</span>
                                    ) : (
                                      <span className="no-sawoff">—</span>
                                    )}
                                  </div>
                                  <div className="col-total">
                                    <span className="total-amount">${player.totalPrize.toFixed(2)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-players-message">
                              <p>No players received prizes in this round.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Verification */}
          <div className="prize-section verification-section">
            <h3>✅ Verification</h3>
            <div className="verification-grid">
              <div className="verification-item">
                <span className="label">Total Distributed:</span>
                <span className="value">
                  ${(summary.totalSawOffsPaid + summary.totalSkinsPaid).toFixed(2)}
                </span>
              </div>
              <div className="verification-item">
                <span className="label">House Keeps:</span>
                <span className="value">${summary.houseKeeps.toFixed(2)}</span>
              </div>
              <div className="verification-item">
                <span className="label">Should Equal Pot:</span>
                <span className="value">${tournament.totalPot.toFixed(2)}</span>
              </div>
              <div className="verification-item">
                <span className="label">Difference:</span>
                <span className={`value ${Math.abs(tournament.totalPot - (summary.totalSawOffsPaid + summary.totalSkinsPaid + summary.houseKeeps)) < 0.01 ? 'success' : 'error'}`}>
                  ${(tournament.totalPot - (summary.totalSawOffsPaid + summary.totalSkinsPaid + summary.houseKeeps)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="prize-note">
            <p>ℹ️ This is a calculation preview. No transactions have been executed. Check browser console for detailed breakdown.</p>
          </div>
        </div>

        <div className="prize-v2-modal-footer">
          <button className="btn-secondary" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrizeV2Modal;

